// src/Components/CategoryStrip.jsx
import React, { useEffect, useRef, useMemo } from "react";

const CategoryStrip = ({
  categories,
  isLoading,
  selectedCategoryId,
  onCategoryClick,
}) => {
  const trackRef = useRef(null);

  // Duplicate the array once for smooth infinite loop
  const loopCategories = useMemo(() => {
    if (!categories || categories.length === 0) return [];
    return [...categories, ...categories]; // duplicate
  }, [categories]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    let frameId;
    const SPEED = 0.6; // px per frame

    const step = () => {
      el.scrollLeft += SPEED;

      const singleWidth = el.scrollWidth / 2;

      // When half is reached → jump back, but user never sees the jump
      if (el.scrollLeft >= singleWidth) {
        el.scrollLeft = 0;
      }

      frameId = requestAnimationFrame(step);
    };

    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [categories.length]);

  return (
    <div className="pt-3" role="region" aria-label="Browse categories">
      <div
        ref={trackRef}
        className="flex gap-2 overflow-x-auto py-1 px-1 -mx-1
          [scrollbar-width:none]
          [-ms-overflow-style:'none']
          [&::-webkit-scrollbar]:hidden
          select-none"
        aria-label="Product categories"
      >
        {/* Loading skeletons */}
        {isLoading &&
          Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="min-w-[110px] h-9 rounded-full bg-sky-100/70 animate-pulse"
            />
          ))}

        {/* Infinite-loop category buttons */}
        {!isLoading &&
          loopCategories.map((category, index) => {
            const active = selectedCategoryId === category._id;

            return (
              <button
                key={`${category._id}-${index}`} // unique per duplicate
                onClick={() => onCategoryClick(category._id)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border transition-all
                  ${
                    active
                      ? "bg-sky-600 text-white border-sky-600 shadow-sm"
                      : "bg-white text-sky-800 border-sky-200 hover:bg-sky-50"
                  }`}
                aria-pressed={active}
              >
                {category.name}
              </button>
            );
          })}
      </div>
    </div>
  );
};

export default CategoryStrip;
